import { ContainerQuery } from "@inwink/responsive/containerquery";
import * as moment from "moment";
import * as momenttimezone from "moment-timezone";
import { DndProvider, DndProviderProps } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Konami } from "@inwink/konami";
import * as React from "react";
import { logging } from "@inwink/logging";
import { actions as userActions } from "@@services/userservice";
import { rightsActions } from "@@services/rightsservice";
import { eventActions } from "@@services/eventservice";
import { customerActions } from "@@services/customerservice";
import { I18NStateContext } from "@@services/i18nservice";

import { AppShellHeader } from "./header/appshellheader";
import { UserProfilePopin } from "./header/appshelluser.popin";
import { Authentication } from "./appshell.authentication";
import { TradsHandler } from "./appshell.trads";
import type { States } from "@@services/services";
import { AppVisualStateContext } from "./appshellcontext";
import type { ILocationMatchProp, ILocationProp } from "@@data/entities";
import { BoThemedModal } from "./ui/modals/bothemedmodal";
import { appthemeActions } from "@@services/appthemeservice";
import { EventAndCommunitySearch } from '@@components/appcommandprompt/eventandcommunitysearchcommand';
import { connectwith } from "@inwink/react-utils/decorators/connectwith";
import { bindActionCreators } from "redux";
import { AppCommandPrompt } from "./appcommandprompt/appcommandprompt";
import {
    AppShellMenuContext,
    AppShellMenuManager,
} from "./appshellmenucontext";
import { AppCommandPromptRegistration, IAppCommandPromptRegistration } from "./appcommandprompt/appcommandpromptregistration";
import { CustomersSearchCommand } from "./appcommandprompt/customerssearchcommand";
import { inwinkSearchCommand } from "./appcommandprompt/appcommandpromptcontext";
import { AlertBanner } from './alertbanner';
import { AppMenu } from "./appmenu";
import { AppShellRoutes } from './appshell.routes';
import "@@styles/transitions.less";
import "@inwink/entityform/entityform.less";
import "@@styles/reset.less";
import "@@styles/basestyles.less";
import "./appshell.less";
import { InwinkBackofficeAgent } from "@@components/aiassistantagent/inwinkbackofficeagent";

// const AppShellRoutesAsync = React.lazy(() => import(/* webpackChunkName: "rootappshell-routes" */"./appshell.routes"));
// const AppShellRoutes = (props) => <React.Suspense><AppShellRoutesAsync {...props} /></React.Suspense>;
// const AppMenu = React.lazy(() => import("./appmenu"));

const logger = logging.getLogger("UI");

const TypedDndProvider: React.FunctionComponent<React.PropsWithChildren<DndProviderProps<any, any>>> = DndProvider as any;

// import(/* webpackChunkName: "initentities" */ "./entities/init").then((mod) => mod.initEntities());

const konamiCode = [38, 38, 40, 40, 37, 39, 37, 39, 66, 65];

(global as any).moment = moment;
(global as any).momenttimezone = momenttimezone;

const mediaquery = {
    XXS: {
        minWidth: 0,
    },
    XS: {
        minWidth: 468,
    },
    S: {
        minWidth: 768,
    },
    M: {
        minWidth: 1024,
    },
    L: {
        minWidth: 1280,
    },
    XL: {
        minWidth: 1690,
    },
};

export interface IAppShellProps {
    event: States.IEventState;
    user: States.IAppUserState;
    i18n: States.i18nState;
    customer: States.ICustomerState;
    community: States.ICommunityState;
    rights: States.IAppUserRights;
    children?: React.ReactNode;
    location: ILocationProp;
    match: ILocationMatchProp;
    routeParams: any;
    eventActions?: typeof eventActions;
    customerActions?: typeof customerActions;
    userActions?: typeof userActions;
    rightsActions?: typeof rightsActions;
    appthemeActions?: typeof appthemeActions;
}

interface IAppShellState {
    visualstate: string[];
    lastvisualstate: string[];
    //menu: IAppShellMenuState;
    shellMounted: boolean;
}

function mapStateToProps(state: States.IAppState) {
    return {
        event: state.event,
        customer: state.customer,
        community: state.community,
        user: state.user,
        i18n: state.i18n,
        rights: state.rights,
        appcontext: state.apptheme,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        eventActions: bindActionCreators(eventActions, dispatch),
        userActions: bindActionCreators(userActions, dispatch),
        rightsActions: bindActionCreators(rightsActions, dispatch),
        customerActions: bindActionCreators(customerActions, dispatch),
        appthemeActions: bindActionCreators(appthemeActions, dispatch),
    };
}

@connectwith(mapStateToProps, mapDispatchToProps)
export class AppShell extends React.Component<IAppShellProps, IAppShellState> {
    commands: IAppCommandPromptRegistration;

    constructor(props: IAppShellProps) {
        super(props);
        this.commands = {
            //standalone: true,
            trigger: inwinkSearchCommand,
            providers: [
                new EventAndCommunitySearch(),
                new CustomersSearchCommand()
            ]
        };
        //this.toggleMenu = this.toggleMenu.bind(this);
        this.konamiCodeActivated = this.konamiCodeActivated.bind(this);
        this.props.appthemeActions.setCurrentTheme("default");

        this.state = {
            // eslint-disable-next-line react/no-unused-state
            lastvisualstate: [],
            visualstate: [],
            shellMounted: false,
        };
    }

    renderLogin() {
        return (
            <I18NStateContext i18n={this.props.i18n}>
                <Authentication {...this.props} />
            </I18NStateContext>
        );
    }

    visualStateChanged = (visualstate: string[]) => {
        const state = { visualstate: visualstate } as any;

        this.setState(state as any);
    };

    currentEnsureEvent: any;

    componentDidMount() {
        // We should change class animation when the AppPage is loaded
        setTimeout(() => {
            this.setState({ shellMounted: true });
        }, 700);
    }

    konamiCodeActivated() {
        this.props.userActions.activateAdvancedMode();
    }

    renderContent() {
        return (
            <>
                <Konami konami={konamiCode} easterEgg={this.konamiCodeActivated} />
                <UserProfilePopin {...this.props}>
                    <div className="app-content">
                        <AppShellHeader
                            {...this.props}
                            visualstate={this.state.visualstate}
                        />
                        <AlertBanner language={this.props.i18n.currentLanguageCode} />
                        <AppShellRoutes
                            {...this.props}
                            visualstate={this.state.visualstate}
                        />
                    </div>
                    <React.Suspense>
                        <AppMenu
                            {...this.props}
                            isMobile={!(this.state.visualstate.indexOf("M") >= 0)}
                        />
                    </React.Suspense>
                </UserProfilePopin>
                <TradsHandler
                    i18n={this.props.i18n}
                    user={this.props.user}
                    rights={this.props.rights}
                />
            </>
        );
    }

    render() {
        if (!this.props.user.currentUser) {
            return this.renderLogin();
        }

        logger.debug("render app shell", this.state.visualstate);

        return (
            <AppCommandPrompt>
                <AppCommandPromptRegistration registration={this.commands}>
                    <TypedDndProvider backend={HTML5Backend}>
                        <AppVisualStateContext.Provider value={this.state.visualstate}>
                            <AppShellMenuManager
                                event={this.props.event}
                                community={this.props.community}
                                rights={this.props.rights}
                                user={this.props.user}
                                isMobile={this.state.visualstate.indexOf("M") <= 0}
                                visualstate={this.state.visualstate}>
                                <InwinkBackofficeAgent>
                                    <AppShellContainerQuery
                                        shellMounted={this.state.shellMounted}
                                        visualStateChanged={this.visualStateChanged}
                                    >
                                        <BoThemedModal>{this.renderContent()}</BoThemedModal>
                                    </AppShellContainerQuery>
                                </InwinkBackofficeAgent>
                            </AppShellMenuManager>
                        </AppVisualStateContext.Provider>
                    </TypedDndProvider>
                </AppCommandPromptRegistration>
            </AppCommandPrompt>
        );
    }
}

function AppShellContainerQuery(props: {
    shellMounted: boolean,
    children?: React.ReactNode,
    visualStateChanged: (state: string[]) => void
}) {
    const menuContext = React.useContext(AppShellMenuContext);

    return (
        <ContainerQuery
            forceEvaluateOnMount={true}
            className={
                (props.shellMounted ? "" : "appload") +
                " app-layout" +
                (menuContext.collapsed ? " menu-collapsed" : " menu-expanded") +
                (menuContext.allowFixed ? " menu-collapsible" : "")
            }
            stateName="applayout"
            query={mediaquery}
            stateChanged={(state) => props.visualStateChanged(state)}
        >
            {props.children}
        </ContainerQuery>
    );
}

export default AppShell;